
import React, { useEffect, useRef, useState } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers';
import { TextBlockView } from '@plone/volto-slate/blocks/Text';
import './multicard.css';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const getImageURL = (image) => {
  if (typeof image === 'string') {
    return `${flattenToAppURL(image)}/@@images/image/large`;
  }
  const imageItem = Array.isArray(image) ? image[0] : image;
  if (imageItem?.['@id']) return `${flattenToAppURL(imageItem['@id'])}/@@images/image/large`;
  if (imageItem?.download) return flattenToAppURL(imageItem.download);
  return null;
};

const getHref = (url, anchor) => {
  const base = typeof url === 'string' ? url : (Array.isArray(url) ? url[0]?.['@id'] : url?.['@id']);
  return anchor?.trim() ? (base ? `${base}${anchor}` : anchor) : base || '#';
};

const isExternalLink = (href) => {
  if (!href) return false;
  if (typeof window === 'undefined') return false;
  try {
    const host = window.location.hostname;
    return href.startsWith('http') && !href.includes(host);
  } catch {
    return false;
  }
};

const isNumericValue = (val) =>
  val !== undefined && val !== null && val !== '' && !isNaN(Number(val));

const renderDescription = (description) => {
  if (!description) return null;
  
  // Check if it's Slate content (object/array with proper structure)
  if (typeof description === 'object' && description !== null) {
    try {
      // Use TextBlockView to render Slate content
      return <TextBlockView data={{ value: description }} />;
    } catch (e) {
      console.error('Error rendering Slate content:', e);
      return null;
    }
  }
  
  // Fallback for plain text (backward compatibility)
  if (typeof description === 'string' && description.trim()) {
    return <p dangerouslySetInnerHTML={{ __html: description.replace(/\n/g, '<br />') }} />;
  }
  
  return null;
};

const CountUp = ({ end = 0, duration = 1500, formatK = false, isPercentage = false }) => {
  const [value, setValue] = useState(0);
  const startRef = useRef(null);
  const endRef = useRef(Number(end) || 0);
  
  // Determine if this is a decimal number
  const isDecimal = endRef.current % 1 !== 0;
  const decimalPlaces = isDecimal ? (endRef.current.toString().split('.')[1] || '').length : 0;

  useEffect(() => {
    endRef.current = Number(end) || 0;
    setValue(0);
    startRef.current = null;

    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      let next = progress * endRef.current;
      
      if (formatK && endRef.current >= 1000 && !isDecimal) {
        // snap to thousands for a clear stepping effect (only for whole numbers)
        next = next - (next % 1000);
      }
      
      setValue(next);
      if (progress < 1) requestAnimationFrame(step);
    };
    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, isDecimal]);

  const formatValue = (val) => {
    if (formatK && endRef.current >= 1000 && !isDecimal) {
      return `${Math.max(1, Math.floor(val / 1000)).toLocaleString()}k`;
    }
    
    if (isDecimal) {
      return val.toFixed(decimalPlaces);
    }
    
    return Math.floor(val).toLocaleString();
  };

  return (
    <span className="stat-value-number">
      {formatValue(value)}
      {isPercentage && '%'}
    </span>
  );
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update));
  }, []);
  return isMobile;
};

const MobileCarousel = ({ enabled, autoplay=true, showDots=true, controlsColor='light', children }) => {
  const isMobile = useIsMobile();
  const plugins = React.useMemo(() => (autoplay ? [Autoplay({ delay: 3500, stopOnMouseEnter: true })] : []), [autoplay]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, plugins);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const slides = React.Children.toArray(children);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi]);

  if (!enabled || !isMobile) return <>{children}</>;
  return (
    <div className={`embla carousel-controls-${controlsColor}`}>
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((slide, i) => (
            <div className="embla__slide" key={i}>{slide}</div>
          ))}
        </div>
      </div>
      {showDots && (
        <div className="embla__dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const View = ({ data }) => {
  const headerAlignmentClass = `align-${data.headerAlignment || 'left'}`;
  const blockAlignmentClass = `align-${data.blockAlignment || 'left'}`;
  const columnClass = `columns-${data.columns || 3}`;
  const textAlignmentClass = `text-align-${data.cardTextAlignment || 'bottom'}`;
  
  // Generate anchor ID for TOC
  const generateSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  };
  
  const anchorId = generateSlug(data.headerText);
  
  // Block background color
  const blockBackgroundColor = data.blockBackgroundColor && data.blockBackgroundColor !== 'none' 
    ? data.blockBackgroundColor 
    : null;
  const blockStyle = blockBackgroundColor ? { backgroundColor: blockBackgroundColor, padding: '3rem 0' } : {};
  
  // Carousel controls color
  const controlsColor = data.carouselControlsColor || 'light';

  if (data.useStatistics) {
    return (
      <div className="multi-card-block" style={blockStyle}>
        {(data.preheaderText?.trim() || data.headerText?.trim() || data.descriptionText?.trim()) && (
          <div className={`multi-card-block-header ${headerAlignmentClass}`} id={anchorId || undefined}>
            {data.preheaderText?.trim() && (
              <div className="multi-card-block-preheader-text">{data.preheaderText}</div>
            )}
            {data.headerText?.trim() && (
              <h2 
                className="multi-card-block-header-text heading"
                dangerouslySetInnerHTML={{ 
                  __html: data.headerText.replace(/\n/g, '<br />') 
                }}
              />
            )}
            {data.descriptionText?.trim() && (
              <p className="multi-card-block-description">{data.descriptionText}</p>
            )}
          </div>
        )}

        <div className={`multi-card-container stats ${blockAlignmentClass} ${columnClass}`}>
        <MobileCarousel 
          enabled={!!data.mobileCarousel} 
          autoplay={data.mobileCarouselAutoplay !== false} 
          showDots={data.mobileCarouselDots !== false}
          controlsColor={controlsColor}
        >
          {(data.statistics || []).map((stat, idx) => {
            const href = getHref(stat.url, stat.anchor);
            const external = isExternalLink(href);
            
            // Parse background color and theme from format: 'color|theme'
            let backgroundColor = null;
            let themeClass = '';
            
            if (data.cardBackgroundColor?.trim()) {
              const parts = data.cardBackgroundColor.split('|');
              backgroundColor = parts[0]; // e.g., 'var(--darkblue)' or '#ffffff'
              const theme = parts[1]; // 'light' or 'dark'
              
              // Apply theme class for text styling
              if (theme === 'dark') {
                themeClass = 'theme-dark'; // light text on dark background
              } else {
                themeClass = 'theme-light'; // dark text on light background
              }
            }
            
            const statStyle = backgroundColor ? { backgroundColor } : {};
            
            const content = (
              <div className={`multi-stat ${themeClass}`.trim()} style={statStyle} key={idx}>
                {stat.label?.trim() && <div className="stat-label">{stat.label}</div>}
                <div className="stat-value">
                  {isNumericValue(stat.value) ? (
                    <CountUp 
                      end={stat.value} 
                      duration={data.statAnimationMs || 6000} 
                      formatK={!!data.statsFormatK}
                      isPercentage={!!stat.isPercentage}
                    />
                  ) : (
                    <span className="stat-value-number">{stat.value}</span>
                  )}
                  {isNumericValue(stat.value) && !stat.isPercentage && stat.suffix?.trim() && (
                    <span className="stat-suffix">{stat.suffix}</span>
                  )}
                </div>
                {stat.extraInfo?.trim() && <div className="stat-extra">{stat.extraInfo}</div>}
              </div>
            );
            return stat.url || stat.anchor ? (
              <a
                key={idx}
                className="multi-stat-link"
                href={href}
                target={external ? '_blank' : '_self'}
                rel={external ? 'noopener noreferrer' : undefined}
              >
                {content}
              </a>
            ) : (
              content
            );
          })}
        </MobileCarousel>
      </div>
      </div>
    );
  }

  return (
    <div className="multi-card-block" style={blockStyle}>
      {(data.preheaderText?.trim() || data.headerText?.trim() || data.descriptionText?.trim()) && (
        <div className={`multi-card-block-header ${headerAlignmentClass}`} id={anchorId || undefined}>
          {data.preheaderText?.trim() && (
            <div className="multi-card-block-preheader-text">{data.preheaderText}</div>
          )}
          {data.headerText?.trim() && (
            <h2 
              className="multi-card-block-header-text heading"
              dangerouslySetInnerHTML={{ 
                __html: data.headerText.replace(/\n/g, '<br />') 
              }}
            />
          )}
          {data.descriptionText?.trim() && (
            <p className="multi-card-block-description">{data.descriptionText}</p>
          )}
        </div>
      )}

      <div className={`multi-card-container ${blockAlignmentClass} ${columnClass} ${textAlignmentClass}`}>
      <MobileCarousel 
        enabled={!!data.mobileCarousel} 
        autoplay={data.mobileCarouselAutoplay !== false} 
        showDots={data.mobileCarouselDots !== false}
        controlsColor={controlsColor}
      >
        {(data.items || []).map((item, index) => {
          const imageUrl = getImageURL(item.image);
          const href = getHref(item.url, item.anchor);
          const external = isExternalLink(href);
          const imagePosition = data.imagePosition || 'background';
          const imageClass = `image-${imagePosition}`;
          
          // Parse background color and theme from format: 'color|theme'
          let backgroundColor = null;
          let themeClass = '';
          
          if (imagePosition !== 'background' && data.cardBackgroundColor?.trim()) {
            const parts = data.cardBackgroundColor.split('|');
            backgroundColor = parts[0]; // e.g., 'var(--darkblue)' or '#ffffff'
            const theme = parts[1]; // 'light' or 'dark'
            
            // Apply theme class for text and button styling
            if (theme === 'dark') {
              themeClass = 'theme-dark'; // light text/buttons on dark background
            } else {
              themeClass = 'theme-light'; // dark text/buttons on light background
            }
          }
          
          // Build inline styles
          const cardStyle = {};
          if (imagePosition === 'background' && imageUrl) {
            cardStyle.backgroundImage = `url(${imageUrl})`;
          } else if (backgroundColor) {
            cardStyle.backgroundColor = backgroundColor;
          }
          
          return (
            <div
              key={index}
              className={`multi-card ${imageClass} ${themeClass}`.trim()}
              style={cardStyle}
            >
              {imagePosition === 'background' && <div className="multi-card-overlay" />}
              
              {imagePosition === 'above-card' && imageUrl && (
                <img src={imageUrl} alt={item.title || ''} className="multi-card-image" />
              )}
              
              <div className="multi-card-content">
                {item.preheader?.trim() && <div className="multi-card-preheader">{item.preheader}</div>}
                {item.title?.trim() && <h2>{item.title}</h2>}
                
                {imagePosition === 'above-content' && imageUrl && (
                  <img src={imageUrl} alt={item.title || ''} className="multi-card-image" />
                )}
                
                {renderDescription(item.description)}
                
                {imagePosition === 'below-content' && imageUrl && (
                  <img src={imageUrl} alt={item.title || ''} className="multi-card-image" />
                )}
                
                {item.linkText?.trim() && (
                  <a
                    href={href}
                    target={external ? '_blank' : '_self'}
                    rel={external ? 'noopener noreferrer' : undefined}
                    className={`button ${item.buttonStyle || 'primary'}`}
                  >
                    {item.linkText}
                  </a>
                )}
              </div>
              
              {imagePosition === 'below-card' && imageUrl && (
                <img src={imageUrl} alt={item.title || ''} className="multi-card-image" />
              )}
            </div>
          );
        })}
      </MobileCarousel>
    </div>
    </div>
  );
};

export default View;
